import { getLastInteraction } from './interactionTracker';

declare global {
  interface Window {
    __BUILD_COMMIT_SHA__?: string;
    __BUILD_RELEASE__?: string;
    __APP_ENV__?: string;
  }
}

export type PortalErrorSource =
  | 'react_error_boundary'
  | 'window_error'
  | 'unhandled_rejection'
  | 'api_request'
  | 'workflow';

export type PortalErrorClassification = 'portal_defect' | 'connectivity' | 'user_actionable';

export type PortalErrorInput = {
  source: PortalErrorSource;
  error?: unknown;
  message?: string;
  componentStack?: string;
  operation?: string;
  classification?: PortalErrorClassification;
  severity?: 'warning' | 'error';
  route?: string;
  recoveryAttempted?: boolean;
  context?: Record<string, string | number | boolean | null | undefined>;
};

export type PortalErrorReceipt = {
  errorId: string;
  fingerprint: string;
};

type PortalErrorPayload = {
  app: 'venue-portal';
  source: PortalErrorSource;
  classification: PortalErrorClassification;
  severity: 'warning' | 'error';
  route: string;
  operation?: string;
  fingerprint: string;
  session_id: string;
  timestamp: string;
  error: {
    name: string;
    message: string;
    stack?: string;
    component_stack?: string;
  };
  build: {
    environment: string;
    commit_sha: string;
    release: string;
  };
  client_state: {
    online: boolean;
    visibility: string;
    viewport?: { width: number; height: number };
    recovery_attempted: boolean;
  };
  last_interaction?: {
    type?: string;
    tag?: string;
    id?: string;
    role?: string;
    page?: string;
    timestamp?: string;
  };
  context?: Record<string, string | number | boolean | null>;
};

const APP_IDENTIFIER = 'venue-portal' as const;
const QUEUE_KEY = 'portal-error-queue-v1';
const SESSION_KEY = 'portal-error-session-v1';
const MAX_QUEUE_ITEMS = 5;
const DEDUPE_WINDOW_MS = 60_000;
const recentFingerprints = new Map<string, number>();
let flushPromise: Promise<void> | null = null;

class PortalErrorDeliveryError extends Error {
  status: number;

  constructor(status: number) {
    super(`portal_error_capture_${status}`);
    this.name = 'PortalErrorDeliveryError';
    this.status = status;
  }
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

function sanitizeText(value: unknown, max: number): string {
  return truncate(String(value || '')
    .replace(/data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+/gi, '[redacted-image]')
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
    .replace(/([?&])(token|token_hash|access_token|refresh_token|code)=[^&#\s]+/gi, '$1$2=[redacted]')
    .replace(/https?:\/\/[^\s)]+/gi, '[redacted-url]')
    .replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, '[redacted-email]'), max);
}

export function sanitizePortalRoute(value?: string): string {
  const raw = String(value || (typeof window !== 'undefined' ? window.location.pathname : '/'));
  let path = raw.split(/[?#]/, 1)[0] || '/';
  if (/^https?:\/\//i.test(raw)) {
    try {
      path = new URL(raw).pathname || '/';
    } catch {
      path = '/external-request';
    }
  }
  return truncate(path
    .replace(/^\/s\/[^/]+/i, '/s/:token')
    .replace(/^\/t\/[^/]+/i, '/t/:token')
    .replace(/^\/sign\/documents\/[^/]+/i, '/sign/documents/:token')
    .replace(/^\/pay\/[^/]+/i, '/pay/:token')
      .replace(/^\/venue-intake\/[^/]+/i, '/venue-intake/:token')
      .replace(/^\/q\/[^/]+/i, '/q/:token')
      .replace(/(\/post-event\/public\/)[^/]+/i, '$1:token')
      .replace(/^\/installations\/sign\/completion\/[^/]+/i, '/installations/sign/completion/:token')
      .replace(/(\/installation-completion\/public\/)[^/]+/i, '$1:token')
    .replace(/(\/signature-documents\/public\/)[^/]+/i, '$1:token')
    .replace(/\b[0-9a-f]{8}-[0-9a-f-]{27,}\b/gi, ':id'), 240);
}

function normalizeError(error: unknown, fallback?: string) {
  if (error instanceof Error) {
    return {
      name: sanitizeText(error.name || 'Error', 120) || 'Error',
      message: sanitizeText(error.message || fallback || 'Unexpected portal error', 2_000),
      stack: sanitizeText(error.stack || '', 16_000) || undefined,
    };
  }
  return {
    name: 'Error',
    message: sanitizeText(error || fallback || 'Unexpected portal error', 2_000),
    stack: undefined,
  };
}

function hash(value: string): string {
  let current = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    current ^= value.charCodeAt(index);
    current = Math.imul(current, 16777619);
  }
  return (current >>> 0).toString(16).padStart(8, '0');
}

export function sessionId(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const created = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return 'unavailable';
  }
}

function safeContext(input?: PortalErrorInput['context']) {
  if (!input) return undefined;
  const output: Record<string, string | number | boolean | null> = {};
  for (const [rawKey, value] of Object.entries(input).slice(0, 20)) {
    const key = sanitizeText(rawKey, 80);
    if (!key) continue;
    if (value == null) output[key] = null;
    else if (typeof value === 'boolean' || typeof value === 'number') output[key] = value;
    else output[key] = sanitizeText(value, 240);
  }
  return output;
}

function buildPayload(input: PortalErrorInput): PortalErrorPayload {
  const error = normalizeError(input.error, input.message);
  const route = sanitizePortalRoute(input.route);
  const fingerprint = hash([
    input.source,
    input.operation || '',
    route,
    error.name,
    error.message,
    error.stack?.split('\n').slice(0, 3).join('\n') || '',
  ].join('|'));
  let lastInteraction: ReturnType<typeof getLastInteraction> = null;
  try {
    lastInteraction = getLastInteraction();
  } catch {
    // Diagnostics must never cause another portal error.
  }
  return {
    app: APP_IDENTIFIER,
    source: input.source,
    classification: input.classification || 'portal_defect',
    severity: input.severity || 'error',
    route,
    operation: input.operation ? sanitizeText(input.operation, 120) : undefined,
    fingerprint,
    session_id: sessionId(),
    timestamp: new Date().toISOString(),
    error: {
      ...error,
      component_stack: input.componentStack
        ? sanitizeText(input.componentStack, 16_000)
        : undefined,
    },
    build: {
      environment: sanitizeText(
        (typeof window !== 'undefined' ? window.__APP_ENV__ : '')
          || import.meta.env.VITE_APP_ENV
          || import.meta.env.MODE
          || 'production',
        80,
      ),
      commit_sha: sanitizeText(
        (typeof window !== 'undefined' ? window.__BUILD_COMMIT_SHA__ : '')
          || import.meta.env.VITE_BUILD_COMMIT_SHA
          || '',
        120,
      ),
      release: sanitizeText(
        (typeof window !== 'undefined' ? window.__BUILD_RELEASE__ : '')
          || import.meta.env.VITE_RELEASE
          || '',
        120,
      ),
    },
    client_state: {
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      visibility: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
      viewport: typeof window === 'undefined'
        ? undefined
        : { width: window.innerWidth, height: window.innerHeight },
      recovery_attempted: input.recoveryAttempted === true,
    },
    last_interaction: lastInteraction ? {
      type: sanitizeText(lastInteraction.type, 40),
      tag: sanitizeText(lastInteraction.tag, 40) || undefined,
      id: sanitizeText(lastInteraction.id, 100) || undefined,
      role: sanitizeText(lastInteraction.role, 60) || undefined,
      page: sanitizePortalRoute(lastInteraction.page),
      timestamp: sanitizeText(lastInteraction.timestamp, 50),
    } : undefined,
    context: safeContext(input.context),
  };
}

function resolveApiBase(): string {
  const base = import.meta.env.VITE_API_BASE_URL || '/api';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

async function postPayload(payload: PortalErrorPayload): Promise<PortalErrorReceipt | null> {
  const response = await fetch(`${resolveApiBase()}/client-errors`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  });
  if (!response.ok) throw new PortalErrorDeliveryError(response.status);
  const result = await response.json().catch(() => null) as { errorId?: unknown } | null;
  return typeof result?.errorId === 'string'
    ? { errorId: result.errorId, fingerprint: payload.fingerprint }
    : null;
}

function readQueue(): PortalErrorPayload[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(QUEUE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE_ITEMS) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: PortalErrorPayload[]) {
  if (typeof window === 'undefined') return;
  try {
    if (queue.length === 0) window.localStorage.removeItem(QUEUE_KEY);
    else window.localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_ITEMS)));
  } catch {
    // Storage can be unavailable in private or constrained browser contexts.
  }
}

function queuePayload(payload: PortalErrorPayload) {
  const queue = readQueue().filter((item) => item.fingerprint !== payload.fingerprint);
  queue.push(payload);
  writeQueue(queue);
}

function shouldRetryDelivery(error: unknown): boolean {
  return !(error instanceof PortalErrorDeliveryError && error.status >= 400 && error.status < 500);
}

export async function flushPendingPortalErrors(): Promise<void> {
  if (flushPromise) return flushPromise;
  flushPromise = (async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    const queue = readQueue();
    const pending: PortalErrorPayload[] = [];
    for (let index = 0; index < queue.length; index += 1) {
      try {
        await postPayload(queue[index]);
      } catch (error) {
        if (shouldRetryDelivery(error)) {
          pending.push(...queue.slice(index));
          break;
        }
      }
    }
    writeQueue(pending);
  })().finally(() => {
    flushPromise = null;
  });
  return flushPromise;
}

export async function capturePortalError(input: PortalErrorInput): Promise<PortalErrorReceipt | null> {
  const payload = buildPayload(input);
  const now = Date.now();
  const lastSentAt = recentFingerprints.get(payload.fingerprint) || 0;
  if (now - lastSentAt < DEDUPE_WINDOW_MS) return null;
  recentFingerprints.set(payload.fingerprint, now);
  for (const [fingerprint, sentAt] of recentFingerprints) {
    if (now - sentAt > DEDUPE_WINDOW_MS * 2) recentFingerprints.delete(fingerprint);
  }
  try {
    const receipt = await postPayload(payload);
    void flushPendingPortalErrors();
    return receipt;
  } catch (error) {
    if (shouldRetryDelivery(error)) queuePayload(payload);
    return null;
  }
}
